import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { auditLogService } from '@/services/auditLogService';
import { locationService } from './locationService';

export interface AssetTypeField {
  id?: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'tel' | 'textarea' | 'loan_officer_picker' | 'alphanumeric' | string;
  required?: boolean;
  description?: string;
  // Metadata for custom field types (e.g., roleFilter for user picker types)
  metadata?: {
    roleFilter?: string;        // For user picker types - which owner role to filter by
    organizationType?: string;   // Which org type this field type belongs to
    component?: string;          // UI component to use
    validation?: {
      pattern?: string;
      minLength?: number;
      maxLength?: number;
    };
  };
}

export interface AssetType {
  id?: string;
  name: string;
  description?: string;
  complianceTemplateIds?: string[]; // Array of compliance template IDs this asset type is tied to
  customFields?: AssetTypeField[]; // Custom fields for this asset type
  evidenceFields?: any; // Aggregated evidence fields from checklists (managed by assetTypeFieldsService)
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

export interface AssetValue {
  id?: string;
  assetTypeId: string; // Reference to asset type
  locationId: string; // Reference to location
  value?: string; // Text value
  fileUrl?: string; // File URL if uploaded
  fileName?: string; // Original file name
  notes?: string; // Additional notes
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

/** Position on a floor map (normalized 0-1: x=left→right, y=top→bottom) */
export interface FloorMapPosition {
  x: number;
  y: number;
}

export interface Asset {
  id?: string;
  assetTypeId: string; // Reference to asset type
  locationId: string; // Reference to location
  assetName: string; // Name of the asset instance
  assetLocation?: string; // Location within the site (e.g., "Room 101", "Front Desk")
  /** Floor ID - which floor this asset is on (for floor map display) */
  floorId?: string;
  /** Position on floor map (normalized 0-1) - used when displaying on floor plan */
  floorMapPosition?: FloorMapPosition;
  photoUrl?: string; // Photo URL if uploaded
  photoFileName?: string; // Original photo file name
  fieldValues?: Record<string, string>; // Custom field values (fieldName -> value)
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export const assetService = {
  // Get all asset types for an organization
  getAllAssetTypes: async (organizationId?: string) => {
    try {
      const assetTypesRef = collection(db, 'asset_types');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        q = query(
          assetTypesRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(assetTypesRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const assetTypes: AssetType[] = [];
      querySnapshot.forEach((doc) => {
        assetTypes.push({ id: doc.id, ...doc.data() } as AssetType);
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        assetTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: assetTypes };
    } catch (error: any) {
      console.error('Error getting all asset types:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single asset type by ID
  getAssetType: async (id: string) => {
    try {
      const assetTypeRef = doc(db, 'asset_types', id);
      const docSnap = await getDoc(assetTypeRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as AssetType };
      } else {
        return { success: false, error: 'Asset type not found' };
      }
    } catch (error: any) {
      console.error('Error getting asset type:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new asset type
  addAssetType: async (assetTypeData: Omit<AssetType, 'id'>, organizationId?: string) => {
    try {
      const assetTypesRef = collection(db, 'asset_types');
      const cleanedData = removeUndefined({
        ...assetTypeData,
        ...(organizationId && { organizationId }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docRef = await addDoc(assetTypesRef, cleanedData);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding asset type:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing asset type
  updateAssetType: async (id: string, assetTypeData: Partial<AssetType>) => {
    try {
      const assetTypeRef = doc(db, 'asset_types', id);
      const cleanedData = removeUndefined({
        ...assetTypeData,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(assetTypeRef, cleanedData);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating asset type:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an asset type
  deleteAssetType: async (id: string) => {
    try {
      const assetTypeRef = doc(db, 'asset_types', id);
      await deleteDoc(assetTypeRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting asset type:', error);
      return { success: false, error: error.message };
    }
  },

  // Copy an asset type
  copyAssetType: async (id: string) => {
    try {
      const assetTypeRef = doc(db, 'asset_types', id);
      const docSnap = await getDoc(assetTypeRef);
      if (docSnap.exists()) {
        const originalData = docSnap.data() as AssetType;
        const newAssetTypeData: Omit<AssetType, 'id'> = {
          ...originalData,
          name: `${originalData.name} (Copy)`,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const assetTypesRef = collection(db, 'asset_types');
        const newDocRef = await addDoc(assetTypesRef, newAssetTypeData);
        return { success: true, id: newDocRef.id };
      } else {
        return { success: false, error: 'Asset type not found' };
      }
    } catch (error: any) {
      console.error('Error copying asset type:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all asset values for a specific asset type
  getAssetValuesByType: async (assetTypeId: string, organizationId?: string) => {
    try {
      const assetValuesRef = collection(db, 'asset_values');
      let q;
      if (organizationId) {
        q = query(
          assetValuesRef,
          where('assetTypeId', '==', assetTypeId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(
          assetValuesRef,
          where('assetTypeId', '==', assetTypeId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const assetValues: AssetValue[] = [];
      querySnapshot.forEach((doc) => {
        assetValues.push({ id: doc.id, ...doc.data() } as AssetValue);
      });
      
      return { success: true, data: assetValues };
    } catch (error: any) {
      console.error('Error getting asset values by type:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all asset values for a specific location
  getAssetValuesByLocation: async (locationId: string, organizationId?: string) => {
    try {
      const assetValuesRef = collection(db, 'asset_values');
      let q;
      if (organizationId) {
        q = query(
          assetValuesRef,
          where('locationId', '==', locationId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(
          assetValuesRef,
          where('locationId', '==', locationId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const assetValues: AssetValue[] = [];
      querySnapshot.forEach((doc) => {
        assetValues.push({ id: doc.id, ...doc.data() } as AssetValue);
      });
      
      return { success: true, data: assetValues };
    } catch (error: any) {
      console.error('Error getting asset values by location:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single asset value by ID
  getAssetValue: async (id: string) => {
    try {
      const assetValueRef = doc(db, 'asset_values', id);
      const docSnap = await getDoc(assetValueRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as AssetValue };
      } else {
        return { success: false, error: 'Asset value not found' };
      }
    } catch (error: any) {
      console.error('Error getting asset value:', error);
      return { success: false, error: error.message };
    }
  },

  // Add or update an asset value
  // This will check if a value already exists for this assetTypeId + locationId combination
  addOrUpdateAssetValue: async (assetValueData: Omit<AssetValue, 'id'>, organizationId?: string) => {
    try {
      // First, check if a value already exists for this combination
      const existingValuesResult = await assetService.getAssetValuesByType(
        assetValueData.assetTypeId,
        organizationId
      );
      
      if (existingValuesResult.success && existingValuesResult.data) {
        const existingValue = existingValuesResult.data.find(
          val => val.locationId === assetValueData.locationId
        );
        
        if (existingValue && existingValue.id) {
          // Update existing value
          const cleanedData = removeUndefined({
            ...assetValueData,
            updatedAt: serverTimestamp(),
          });
          await updateDoc(doc(db, 'asset_values', existingValue.id), cleanedData);
          return { success: true, id: existingValue.id, isUpdate: true };
        }
      }
      
      // No existing value found, create new one
      const assetValuesRef = collection(db, 'asset_values');
      const cleanedData = removeUndefined({
        ...assetValueData,
        ...(organizationId && { organizationId }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docRef = await addDoc(assetValuesRef, cleanedData);
      return { success: true, id: docRef.id, isUpdate: false };
    } catch (error: any) {
      console.error('Error adding or updating asset value:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an asset value
  deleteAssetValue: async (id: string) => {
    try {
      const assetValueRef = doc(db, 'asset_values', id);
      await deleteDoc(assetValueRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting asset value:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all assets for a specific asset type
  getAssetsByType: async (assetTypeId: string, organizationId?: string) => {
    try {
      const assetsRef = collection(db, 'assets');
      let q;
      if (organizationId) {
        q = query(
          assetsRef,
          where('assetTypeId', '==', assetTypeId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(
          assetsRef,
          where('assetTypeId', '==', assetTypeId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const assets: Asset[] = [];
      querySnapshot.forEach((doc) => {
        assets.push({ id: doc.id, ...doc.data() } as Asset);
      });
      
      return { success: true, data: assets };
    } catch (error: any) {
      console.error('Error getting assets by type:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all assets for a specific location
  getAssetsByLocation: async (locationId: string, organizationId?: string) => {
    try {
      const assetsRef = collection(db, 'assets');
      let q;
      if (organizationId) {
        q = query(
          assetsRef,
          where('locationId', '==', locationId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(
          assetsRef,
          where('locationId', '==', locationId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const assets: Asset[] = [];
      querySnapshot.forEach((doc) => {
        assets.push({ id: doc.id, ...doc.data() } as Asset);
      });
      
      return { success: true, data: assets };
    } catch (error: any) {
      console.error('Error getting assets by location:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all assets for a specific floor (with floor map position)
  getAssetsByFloor: async (floorId: string, organizationId?: string) => {
    try {
      const assetsRef = collection(db, 'assets');
      let q;
      if (organizationId) {
        q = query(
          assetsRef,
          where('floorId', '==', floorId),
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(assetsRef, where('floorId', '==', floorId));
      }
      const snapshot = await getDocs(q);
      const assets: Asset[] = [];
      snapshot.forEach((d) => {
        assets.push({ id: d.id, ...d.data() } as Asset);
      });
      return { success: true, data: assets };
    } catch (error: any) {
      console.error('Error getting assets by floor:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single asset by ID
  getAsset: async (id: string) => {
    try {
      const assetRef = doc(db, 'assets', id);
      const docSnap = await getDoc(assetRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as Asset };
      } else {
        return { success: false, error: 'Asset not found' };
      }
    } catch (error: any) {
      console.error('Error getting asset:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new asset
  addAsset: async (
    assetData: Omit<Asset, 'id'>, 
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const assetsRef = collection(db, 'assets');
      // Trim whitespace from asset name before saving
      const trimmedAssetData = {
        ...assetData,
        assetName: assetData.assetName?.trim() || '',
        ...(assetData.assetLocation && { assetLocation: assetData.assetLocation.trim() }),
      };
      const cleanedData = removeUndefined({
        ...trimmedAssetData,
        ...(organizationId && { organizationId }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docRef = await addDoc(assetsRef, cleanedData);
      
      // Log audit entry
      if (userInfo && organizationId) {
        // Get location name for audit log
        let locationName = 'Unknown';
        if (assetData.locationId) {
          const locResult = await locationService.getLocation(assetData.locationId);
          if (locResult.success && locResult.data) {
            locationName = locResult.data.name;
          }
        }
        
        await auditLogService.log({
          action: 'asset_added',
          entityType: 'asset',
          entityId: docRef.id,
          entityName: assetData.assetName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
          details: {
            locationId: assetData.locationId,
            locationName: locationName,
          },
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding asset:', error);
      return { success: false, error: error.message };
    }
  },

  // Add multiple assets from CSV/Excel data
  addAssetsBulk: async (assetsData: Array<Omit<Asset, 'id'>>, organizationId?: string) => {
    try {
      const assetsRef = collection(db, 'assets');
      const results: string[] = [];
      
      for (const assetData of assetsData) {
        // Trim whitespace from asset name before saving
        const trimmedAssetData = {
          ...assetData,
          assetName: assetData.assetName?.trim() || '',
          ...(assetData.assetLocation && { assetLocation: assetData.assetLocation.trim() }),
        };
        const cleanedData = removeUndefined({
          ...trimmedAssetData,
          ...(organizationId && { organizationId }),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        const docRef = await addDoc(assetsRef, cleanedData);
        results.push(docRef.id);
      }
      
      return { success: true, ids: results };
    } catch (error: any) {
      console.error('Error adding assets bulk:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing asset
  updateAsset: async (
    id: string, 
    assetData: Partial<Asset>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get current asset for audit log
      let currentAsset: Asset | null = null;
      if (userInfo && organizationId) {
        const currentResult = await assetService.getAsset(id);
        if (currentResult.success && currentResult.data) {
          currentAsset = currentResult.data;
        }
      }
      
      const assetRef = doc(db, 'assets', id);
      // Trim whitespace from asset name if being updated
      const trimmedAssetData: Partial<Asset> = {
        ...assetData,
        ...(assetData.assetName && { assetName: assetData.assetName.trim() }),
        ...(assetData.assetLocation && { assetLocation: assetData.assetLocation.trim() }),
      };
      const cleanedData = removeUndefined({
        ...trimmedAssetData,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(assetRef, cleanedData);
      
      // Log audit entries for changes
      if (userInfo && organizationId && currentAsset) {
        // Check for location change (locationId)
        if (assetData.locationId !== undefined && assetData.locationId !== currentAsset.locationId) {
          // Get location names
          let oldLocationName = 'Unknown';
          let newLocationName = 'Unknown';
          
          if (currentAsset.locationId) {
            const oldLocResult = await locationService.getLocation(currentAsset.locationId);
            if (oldLocResult.success && oldLocResult.data) {
              oldLocationName = oldLocResult.data.name;
            }
          }
          
          if (assetData.locationId) {
            const newLocResult = await locationService.getLocation(assetData.locationId);
            if (newLocResult.success && newLocResult.data) {
              newLocationName = newLocResult.data.name;
            }
          }
          
          await auditLogService.log({
            action: 'asset_location_changed',
            entityType: 'asset',
            entityId: id,
            entityName: assetData.assetName || currentAsset.assetName,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldValue: currentAsset.locationId,
              newValue: assetData.locationId,
              locationId: assetData.locationId,
              locationName: newLocationName,
            },
          });
        }
        
        // Check for compliance change (assetTypeId)
        if (assetData.assetTypeId !== undefined && assetData.assetTypeId !== currentAsset.assetTypeId) {
          // Get asset type names (which relate to compliance)
          let oldAssetTypeName = 'Unknown';
          let newAssetTypeName = 'Unknown';
          
          if (currentAsset.assetTypeId) {
            const oldTypeResult = await assetService.getAssetType(currentAsset.assetTypeId);
            if (oldTypeResult.success && oldTypeResult.data) {
              oldAssetTypeName = oldTypeResult.data.name;
            }
          }
          
          if (assetData.assetTypeId) {
            const newTypeResult = await assetService.getAssetType(assetData.assetTypeId);
            if (newTypeResult.success && newTypeResult.data) {
              newAssetTypeName = newTypeResult.data.name;
            }
          }
          
          await auditLogService.log({
            action: 'asset_compliance_changed',
            entityType: 'asset',
            entityId: id,
            entityName: assetData.assetName || currentAsset.assetName,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldComplianceId: currentAsset.assetTypeId,
              newComplianceId: assetData.assetTypeId,
              oldComplianceName: oldAssetTypeName,
              newComplianceName: newAssetTypeName,
            },
          });
        }
        
        // Log general update (if not just a location or compliance change)
        if ((assetData.locationId === undefined || assetData.locationId === currentAsset.locationId) &&
            (assetData.assetTypeId === undefined || assetData.assetTypeId === currentAsset.assetTypeId)) {
          await auditLogService.log({
            action: 'asset_updated',
            entityType: 'asset',
            entityId: id,
            entityName: assetData.assetName || currentAsset.assetName,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldValue: currentAsset,
              newValue: { ...currentAsset, ...cleanedData },
            },
          });
        }
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating asset:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an asset
  deleteAsset: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get asset info before deletion for audit log
      let assetName = 'Unknown';
      if (userInfo && organizationId) {
        const assetResult = await assetService.getAsset(id);
        if (assetResult.success && assetResult.data) {
          assetName = assetResult.data.assetName;
        }
      }
      
      const assetRef = doc(db, 'assets', id);
      await deleteDoc(assetRef);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'asset_deleted',
          entityType: 'asset',
          entityId: id,
          entityName: assetName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting asset:', error);
      return { success: false, error: error.message };
    }
  },

  // Remove floor map tag from an asset
  removeFloorTag: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      const assetRef = doc(db, 'assets', id);
      await updateDoc(assetRef, {
        floorId: deleteField(),
        floorMapPosition: deleteField(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error removing floor tag:', error);
      return { success: false, error: error.message };
    }
  },
};
